import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Conduct = () => {
  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <span className="sr-only-seo">human on rent code of conduct, boyfriend on rent rules, girlfriend on rent guidelines</span>

      <Header />

      <main className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-light text-gray-900 mb-4">Code of Conduct</h1>
          <p className="text-xl text-gray-600 mb-12">
            Our community guidelines ensure a safe, respectful, and positive experience for everyone.
          </p>

          {/* Core Principles */}
          <div className="bg-[#111827] text-white rounded-3xl p-8 mb-12">
            <h2 className="text-2xl font-medium mb-6">Core Principles</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "Respect every individual regardless of background",
                "Maintain professional conduct at all times",
                "Ensure safety and comfort of all parties",
                "Communicate honestly and transparently"
              ].map((principle, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-gray-300">{principle}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="legal-content">
            <h2>Expected Behavior</h2>
            <div className="space-y-4 mb-8">
              {[
                "Be punctual for all scheduled meetings",
                "Dress appropriately for the occasion",
                "Communicate clearly and respond promptly",
                "Respect personal boundaries and space",
                "Follow through on commitments",
                "Provide honest feedback after services",
                "Report any concerns to our support team",
                "Treat providers/hirers with dignity"
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <h2>Prohibited Behavior</h2>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
              <p className="text-red-800 font-medium mb-4">
                The following behaviors will result in immediate account suspension or permanent ban:
              </p>
              <div className="space-y-3">
                {[
                  "Any form of harassment, abuse, or intimidation",
                  "Requesting or engaging in non-platonic services",
                  "Physical, verbal, or emotional misconduct",
                  "Discrimination based on race, religion, gender, or any other characteristic",
                  "Sharing explicit content or making inappropriate advances",
                  "Fraudulent activities or misrepresentation",
                  "Attempting to circumvent platform payments",
                  "Stalking or contacting users outside the platform inappropriately",
                  "Consuming alcohol or drugs during bookings (unless agreed upon for bar/club services)",
                  "Violence or threats of violence"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-red-900">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <h2>For Providers</h2>
            <h3>Professional Standards</h3>
            <ul>
              <li>Maintain a professional appearance appropriate to the service</li>
              <li>Arrive on time or notify the hirer in advance of delays</li>
              <li>Provide the service as described in your profile</li>
              <li>Do not solicit additional services or payments outside the platform</li>
              <li>Keep personal information confidential</li>
            </ul>

            <h3>Cancellation Etiquette</h3>
            <ul>
              <li>Cancel only for genuine emergencies</li>
              <li>Provide as much notice as possible (minimum 12 hours)</li>
              <li>Give a valid reason when canceling</li>
              <li>Repeated cancellations may result in account penalties</li>
            </ul>

            <h2>For Hirers</h2>
            <h3>Booking Responsibilities</h3>
            <ul>
              <li>Provide accurate booking details (location, time, purpose)</li>
              <li>Respect the provider's time and boundaries</li>
              <li>Do not request services outside the agreed scope</li>
              <li>Make payment through the platform only</li>
              <li>Provide a safe environment for the service</li>
            </ul>

            <h3>During the Service</h3>
            <ul>
              <li>Treat the provider with respect and dignity</li>
              <li>Do not pressure for personal contact information</li>
              <li>Communicate any concerns immediately</li>
              <li>Mark the booking complete after service</li>
            </ul>

            <h2>Safety Guidelines</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-2">Stay Safe</h3>
                  <ul className="text-amber-800 space-y-2">
                    <li>• Always meet in public places initially</li>
                    <li>• Share your location with a trusted contact</li>
                    <li>• Trust your instincts - leave if uncomfortable</li>
                    <li>• Use the SOS feature in case of emergency</li>
                    <li>• Report suspicious behavior immediately</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2>Reporting Violations</h2>
            <p>To report a violation of this Code of Conduct:</p>
            <ul>
              <li>Use the "Report" button in the booking details</li>
              <li>Email: safety@humanonrent.com</li>
              <li>Emergency: Use SOS feature or call +91 98765 43210</li>
            </ul>
            <p>All reports are investigated within 24-48 hours. Your identity will be kept confidential.</p>

            <h2>Consequences</h2>
            <p>Violations may result in:</p>
            <ul>
              <li><strong>Warning:</strong> First minor offense</li>
              <li><strong>Temporary Suspension:</strong> Repeated minor offenses or single major offense</li>
              <li><strong>Permanent Ban:</strong> Serious violations or repeated offenses</li>
              <li><strong>Legal Action:</strong> Criminal activities will be reported to authorities</li>
            </ul>

            <h2>Commitment</h2>
            <p>
              By using HumanOnRent.AI, you agree to abide by this Code of Conduct. 
              Together, we can build a safe, respectful, and positive community for everyone.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Conduct;
